// THE MAGNET — one offer, one SKU, one switch.
//
// The growth strategy (docs/GROWTH_STRATEGY_2026-08.md, Leg I §4) is explicit:
// the email magnet is "one offer for the primary SKU", not a catalogue sampler.
// A sampler asks the reader to choose; a single offer asks them to decide. Only
// the second one produces the fifty opt-ins scoreboard line 3 wants.
//
// ─────────────────────────────────────────────────────────────────────────
// TO SWITCH THE OFFER: change ACTIVE_OFFER_ID on the next line. That is the
// whole change. Title, art, promise, deliverable and the SKU it feeds all move
// with it; /offer, the /go links and the /x article footers all follow.
// After switching, re-run:  scripts/mint-site-links.py
// ─────────────────────────────────────────────────────────────────────────
export const ACTIVE_OFFER_ID = "art_wallpaper_pack";

// PROVISIONAL. Start-lock condition #1 says the primary SKU is named by the
// buyer/non-buyer interviews — the French art buyer, the US refunder, the Etsy
// favouriters, the two recorded subscribers — and not by this file. Until those
// notes exist, `art_wallpaper_pack` is the placeholder with the least invented
// in it: art is the only vertical that has shown positive contribution, and the
// only paid lane Phase A opens. It is a default, not a decision.
export const OFFER_IS_PROVISIONAL = true;

// Every offer below is complete and shippable. Switching between them is the
// one-line change above — deliberately, so a founder decision costs a commit
// and not a rebuild.
export const OFFERS = {
  // ── Art ───────────────────────────────────────────────────────────────
  art_wallpaper_pack: {
    id: "art_wallpaper_pack",
    // products.optimizer_slug this magnet feeds. Art prints have no row in
    // `products` yet (the table carries app/book/comic/film/song), so this
    // names the vertical; set it to the real slug once art is a product row.
    sku: "art_prints",
    vertical: "art",
    lane: "owned",

    eyebrow: "A gift, freely given",
    title: "Five sacred wallpapers for your phone.",
    subtitle:
      "Painted the slow way — oil-glaze light, warm gold against cool indigo. Sized for the screen you actually look at forty times a day.",
    promise: [
      "Five pieces, three sizes each — phone, tablet and desktop. No watermark.",
      "Yours to keep, whether or not you ever buy a print.",
      "One short letter a week. Nothing else, ever.",
    ],

    art: {
      hero: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/shop/krishna-beautiful-portrait-thumb.jpg",
      alt: "Krishna, peacock-crowned, in a soft contemplative gaze — oil-glaze illumination in gold and indigo",
      orientation: "portrait",
    },

    // What actually gets handed over. See DELIVERABLE CONTRACT below.
    deliverable: {
      label: "Five wallpapers, one download",
      format: "ZIP",
      size_label: "~15 MB",
      r2: { product_slug: "wallpaper-pack-01", file: "dharmaweave-wallpapers-01.zip" },
    },

    // Where the reader goes next if they want more than the free thing.
    onward: { label: "See the prints", to: "/shop" },
  },

  // ── Graphic novel ─────────────────────────────────────────────────────
  gn_first_chapter: {
    id: "gn_first_chapter",
    sku: "varaha",
    vertical: "books",
    lane: "owned",

    eyebrow: "Read the first chapter",
    title: "Varaha — the first chapter, free.",
    subtitle:
      "When the demon drags the Earth to the floor of the cosmic ocean, Vishnu descends as the great boar. Twenty painted pages, from the Bhagavata Purana.",
    promise: [
      "Chapter one, full resolution, as a PDF.",
      "No account, no app — the file is yours.",
      "One short letter a week. Nothing else, ever.",
    ],

    art: {
      hero: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/website/books/varaha/cover.jpg",
      alt: "Varaha, Vishnu's boar avatar, raising the drowned Earth on his tusk",
      orientation: "portrait",
    },

    deliverable: {
      label: "Chapter one, as a PDF",
      format: "PDF",
      size_label: "~12 MB",
      r2: { product_slug: "varaha", file: "varaha-chapter-1.pdf" },
    },

    onward: { label: "See the whole book", to: "/books/varaha" },
  },

  // ── The new film ──────────────────────────────────────────────────────
  oxherding_chapter: {
    id: "oxherding_chapter",
    sku: "film_oxherding",
    vertical: "film",
    lane: "owned",

    eyebrow: "Before it releases",
    title: "The Ten Ox-Herding Pictures — the first three, early.",
    subtitle:
      "The oldest map of the Zen path: searching for the ox, finding the tracks, glimpsing it. Painted frame by frame for the coming film.",
    promise: [
      "Twenty-six painted pages, full resolution, before anyone else sees them.",
      "A note on what each one is actually saying.",
      "One short letter a week. Nothing else, ever.",
    ],

    art: {
      hero: "https://aiwugigdrvijjeoqtpog.supabase.co/storage/v1/object/public/dharma-images/shop/basho-old-pond-museum-edition-thumb.jpg",
      alt: "A woodblock-tradition plate in the Zen idiom — ink, indigo and a single figure in a wide landscape",
      orientation: "portrait",
    },

    deliverable: {
      label: "Chapter one, as a PDF",
      format: "PDF",
      size_label: "~12 MB",
      r2: { product_slug: "oxherding", file: "oxherding-chapter-1.pdf" },
    },

    onward: { label: "See the films", to: "/watch" },
  },
};

// ─────────────────────────────────────────────────────────────────────────
// DELIVERABLE CONTRACT
//
// `deliverable` describes the thing handed over, and the server decides how.
// Exactly three states, and the page tells the truth about whichever one it is
// in — it never claims to have sent an email it cannot send:
//
//   1. OFFER_ASSET_URL set   → that URL is handed over. One Pages environment
//                              variable, no deploy of this file. The fastest
//                              way to put a real file behind the offer.
//   2. r2 + signing secret   → /api/offer/claim mints a signed, 7-day,
//                              single-file /d/<token> URL. Needs the Pages env
//                              var DOWNLOAD_SIGNING_SECRET and the WALLPAPERS
//                              R2 binding functions/d/[token].js already uses.
//   3. neither resolvable    → the reader is told, in plain words, that they
//                              are on the list and the file is not cut yet.
//                              The subscriber row still lands, with full UTMs.
//
// `r2` here is documentation of intent; the authoritative copy is OFFER_ASSETS
// in functions/api/offer/claim.js, because the browser must not get to name the
// file it receives. Keep the two in step when adding an offer.
//
// NOT WIRED, ON PURPOSE: double opt-in. A confirmation email needs a
// transactional provider, and this studio has none configured (the only SMTP
// credential in the engine is the internal digest mailer, which is not a
// consented-marketing sender). Rather than fake it, the form takes an explicit,
// unticked consent checkbox and records the answer in subscribers.
// consent_marketing. See docs/CAPTURE_ORGAN.md.
// ─────────────────────────────────────────────────────────────────────────

export function activeOffer() {
  const o = OFFERS[ACTIVE_OFFER_ID];
  if (!o) {
    throw new Error(
      `ACTIVE_OFFER_ID "${ACTIVE_OFFER_ID}" is not in OFFERS — the magnet page has no offer to show.`
    );
  }
  return o;
}
